import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, LineChart, Globe, Leaf } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LandingPage = () => {
  const { t, getLocalizedPath } = useLanguage();

  useEffect(() => {
    document.title = "Silvaconsult Registry";
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-[silvaconsult-primary-50]">
      <section className="relative bg-silvaconsult-primary-800 text-white overflow-hidden mx-3 sm:mx-[16px] my-3 sm:my-[16px] rounded-xl flex items-center min-h-[70vh] sm:h-[80vh]">
        <div className="absolute bottom-0 right-0 w-1/3 sm:w-[40%] h-[60%] sm:h-[80%] bg-silvaconsult-primary-700 rounded-tl-[50px] sm:rounded-tl-[100px] opacity-70"></div>

        <div className="container mx-auto max-w-6xl relative z-10 py-8 sm:py-0">
          <div className="grid grid-cols-1 gap-6 sm:gap-8 items-center">
            <div className="space-y-6 max-w-3xl px-4 sm:px-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {t("landingPage.heroSection.title")}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl opacity-90 max-w-2xl leading-relaxed">
                {t("landingPage.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2 sm:pt-4">
                <Link
                  to={getLocalizedPath("/projects")}
                  className="w-full sm:w-auto"
                >
                  <Button
                    size="lg"
                    variant="primary"
                    className="w-full sm:w-auto h-14 bg-silvaconsult-primary-500 text-white hover:bg-silvaconsult-primary-600 px-4 sm:px-6 text-sm font-semibold rounded-xl"
                  >
                    {t("landingPage.exploreProjects")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link
                  to={getLocalizedPath("/transactions")}
                  className="w-full sm:w-auto"
                >
                  <Button
                    size="lg"
                    variant="silvaconsultWhiteOutline"
                    className="w-full sm:w-auto h-14 px-4 sm:px-6 text-silvaconsult-primary-800 bg-white font-semibold hover:bg-silvaconsult-primary-100 text-sm rounded-xl"
                  >
                    {t("common.transactions")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="min-h-screen sm:h-screen flex items-center py-12 sm:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10 sm:mb-16 text-black">
            {t("landingPage.impactSection.title")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10 md:gap-x-16 md:gap-y-12">
            <div className="space-y-10 sm:space-y-12">
              <div className="flex items-start gap-4 sm:gap-5">
                <div className="bg-silvaconsult-primary-100 rounded-full p-3 mt-1 flex-shrink-0">
                  <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-500" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3 text-black">
                    {t("landingPage.impactSection.card1Title")}
                  </h3>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                    {t("landingPage.impactSection.card1Description")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 sm:gap-5">
                <div className="bg-silvaconsult-primary-100 rounded-full p-3 mt-1 flex-shrink-0">
                  <LineChart className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-500" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3 text-black">
                    {t("landingPage.impactSection.card2Title")}
                  </h3>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                    {t("landingPage.impactSection.card2Description")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 sm:gap-5">
                <div className="bg-silvaconsult-primary-100 rounded-full p-3 mt-1 flex-shrink-0">
                  <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-500" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3 text-black">
                    {t("landingPage.impactSection.card3Title")}
                  </h3>
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                    {t("landingPage.impactSection.card3Description")}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center mt-6 md:mt-0">
              <div className="rounded-lg p-0 w-full max-w-xl lg:max-w-2xl">
                <img
                  src="/lovable-uploads/1ab48cbb-b4be-4d0a-938b-1a29a1e2d75b.png"
                  alt="Silvaconsult Projects Dashboard"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 rounded-xl mx-0 my-0 bg-[silvaconsult-primary-100] sm:py-[56px]">
        <div className="container mx-auto max-w-6xl rounded-2xl bg-silvaconsult-primary-100 py-[56px]">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-silvaconsult-primary-800">
            {t("landingPage.projectSpotlightSection.title")}
          </h2>
          <p className="text-base sm:text-lg text-silvaconsult-primary-800 max-w-3xl mb-8 sm:mb-12 leading-relaxed">
            {t("landingPage.description")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl">
            <Link to={getLocalizedPath("/transactions")} className="block">
              <div className="flex flex-col h-full p-6 sm:p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-silvaconsult-primary-200">
                <div className="mb-6">
                  <div className="bg-silvaconsult-primary-100 rounded-full p-3 inline-flex">
                    <LineChart className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-600" />
                  </div>
                </div>
                <div className="flex items-center mt-auto">
                  <span className="font-semibold text-silvaconsult-primary-700 mr-2">
                    {t("common.transactions")}
                  </span>
                  <ArrowRight className="h-4 w-4 text-silvaconsult-primary-500" />
                </div>
              </div>
            </Link>

            <Link to={getLocalizedPath("/projects")} className="block">
              <div className="flex flex-col h-full p-6 sm:p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-silvaconsult-primary-200">
                <div className="mb-6">
                  <div className="bg-silvaconsult-primary-100 rounded-full p-3 inline-flex">
                    <Leaf className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-600" />
                  </div>
                </div>
                <div className="flex items-center mt-auto">
                  <span className="font-semibold text-silvaconsult-primary-700 mr-2">
                    {t("common.projects")}
                  </span>
                  <ArrowRight className="h-4 w-4 text-silvaconsult-primary-500" />
                </div>
              </div>
            </Link>

            <Link to={getLocalizedPath("/organizations")} className="block">
              <div className="flex flex-col h-full p-6 sm:p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-silvaconsult-primary-200">
                <div className="mb-6">
                  <div className="bg-silvaconsult-primary-100 rounded-full p-3 inline-flex">
                    <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-silvaconsult-primary-600" />
                  </div>
                </div>
                <div className="flex items-center mt-auto">
                  <span className="font-semibold text-silvaconsult-primary-700 mr-2">
                    {t("common.organizations")}
                  </span>
                  <ArrowRight className="h-4 w-4 text-silvaconsult-primary-500" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
